import {
  getMomentTz,
  MenuOptionType,
  useHasPermission,
  useShiftNavigator,
} from "@dexteel/mesf-core";
import {
  CallSplit,
  Comment,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Memory as MemoryIcon,
  Straighten,
  Whatshot as WhatshotIcon,
} from "@mui/icons-material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { GanttBackgroundHit } from "../../../../controls/ganttAsset/helpers/gantt-context-hit";
import { getDelayById } from "../../../delays/codes/repositories/ConfigurationDelayRepository";
import type { Process, ProcessEvent } from "../../models/ProcessGanttChart";
import { getNewProcessMenuOption } from "../components/GanttSection/gantt-background-menu";
import type { SelectedProcess } from "../models/SelectedProcess";

const moment = getMomentTz();

interface UseGanttOptionsContextMenuParams {
  setSelectedProcess: (process: SelectedProcess | null) => void;
}

export const useGanttOptionsContextMenu = ({
  setSelectedProcess,
}: UseGanttOptionsContextMenuParams) => {
  const { shiftInfo } = useShiftNavigator();
  const navigate = useNavigate();
  const hasPermission = useHasPermission();

  const [showModal, setShowModal] = useState<
    "edit" | "link" | "unlink" | "delete" | "active" | ""
  >("");
  const [showModalDelay, setShowModalDelay] = useState<
    "justify" | "split" | ""
  >("");
  const [messageDeleteModal, setMessageDeleteModal] = useState("");
  const [messageActiveModal, setMessageActiveModal] = useState("");
  const [messageUnlinkModal, setMessageUnlinkModal] = useState("");

  const [error, setError] = useState<string>("");

  const checkPermission = (
    assetId: number | string | undefined,
    permission: string,
  ): boolean => {
    if (assetId == 11 && !hasPermission(`ui.navbar.eaf.${permission}`))
      return false;
    if (assetId == 12 && !hasPermission(`ui.navbar.lmf.${permission}`))
      return false;
    if (assetId == 13 && !hasPermission(`ui.navbar.caster.${permission}`))
      return false;
    return true;
  };

  const navigateWithPermission = (
    path: string,
    permission: string,
    condition = true,
  ) => {
    if (condition && hasPermission(permission)) {
      navigate(path);
    } else {
      setError("You don't have permission to access this page");
    }
  };

  //Create
  const createData = useCallback(
    (assetId: number | null) => {
      setSelectedProcess({
        ProcessId: 0,
        AssetId: Number(assetId),
        ProcessCode: "",
        Start: shiftInfo?.CurrentProductionDate ?? new Date(),
        End: shiftInfo?.CurrentProductionDate ?? new Date(),
        HeatName: null,
        Comments: null,
      });
      setShowModal("edit");
    },
    [shiftInfo?.CurrentProductionDate, setSelectedProcess],
  );

  //Edit
  const editData = (data: Process) => {
    if (!checkPermission(data.AssetId, "processes")) {
      setError("You don't have permission to edit this process");
      return;
    }
    setSelectedProcess(data);
    setShowModal("edit");
  };

  //Link
  const linkData = (data: Process) => {
    if (!checkPermission(data.AssetId, "processes")) {
      setError("You don't have permission to link this process");
      return;
    }
    setSelectedProcess(data);
    setShowModal("link");
  };

  //Justify
  const justifyData = (data: ProcessEvent) => {
    if (!checkPermission(data.id, "delays-manager")) {
      setError("You don't have permission to justify this delay");
      return;
    }
    setShowModalDelay("justify");
  };

  //Split
  const splitData = (data: ProcessEvent) => {
    if (!checkPermission(data.id, "delays-manager")) {
      setError("You don't have permission to split this delay");
      return;
    }
    setShowModalDelay("split");
  };

  //Unlink
  const unlinkData = (data: Process) => {
    if (!checkPermission(data.AssetId, "processes")) {
      setError("You don't have permission to unlink this process");
      return;
    }
    setSelectedProcess(data);
    const dateOpen = moment.utc(data.Start).toDate();
    setMessageUnlinkModal(
      `The heat ${data.HeatName} from ${moment(dateOpen).format(
        "YYYY/MM/DD HH:mm",
      )} will be unlinked.  Do you want to continue?`,
    );
    setShowModal("unlink");
  };

  const inactiveData = (data: Process) => {
    setSelectedProcess(data);
    const dateOpen = moment.utc(data.Start).toDate();
    if (data.HeatName) {
      setMessageDeleteModal(
        ` The Process starting at ${moment(dateOpen).format(
          "YYYY/MM/DD HH:mm",
        )} associated with heat ${
          data.HeatName
        } will be deleted. Do you want to continue?`,
      );
    } else {
      setMessageDeleteModal(
        ` The Process starting at ${moment(dateOpen).format(
          "YYYY/MM/DD HH:mm",
        )} and not assigned to any heat will be deleted. Do you want to continue?`,
      );
    }
    setShowModal("delete");
  };

  const getGanttMenuOptions = (
    data: Process | GanttBackgroundHit,
  ): MenuOptionType[] => {
    if ("asset" in data) {
      return [getNewProcessMenuOption(data.asset.id, createData, <EditIcon />)];
    }
    let options: MenuOptionType[] = [];
    if (data.ProcessId) {
      options.push({
        name: "See in Data Entry",
        key: "go_to_data_entry",
        style: { fontWeight: "bold" },
        onClick: () => {
          if (data.ProcessCode === "E") {
            goToEAFDataEntry(data.ProcessId);
          } else if (data.ProcessCode === "C") {
            goToCasterDataEntry(data.ProcessId);
          } else if (data.ProcessCode === "L") {
            goToLMFDataEntry(data.ProcessId);
          }
        },
        icon: <MemoryIcon />,
      });
      options.push({
        name: "See in Heat Report",
        key: "go_to_heat_report",
        style: { fontWeight: "bold" },
        onClick: () => {
          goToHeatReport(data);
        },
        icon: <WhatshotIcon />,
      });
    }
    if (data.HeatName && data.ShiftId) {
      options = options.concat([
        {
          name: "See in Heat Table",
          key: "go_to_heat",
          onClick: () => goToHeats(data.ShiftId, data.HeatName!),
          icon: <WhatshotIcon />,
        },
      ]);
    }
    if (data.HeatName && data.ProcessCode === "C") {
      options = options.concat([
        {
          name: "See in Torches DE",
          key: "go_to_torches_data_entry",
          onClick: () => goToTorchesDataEntry(data.ProcessId),
          icon: <Straighten />,
        },
      ]);
    }
    if (data.HeatName) {
      options = options.concat([
        {
          name: "Unlink Process",
          key: "unlink",
          onClick: () => unlinkData(data),
          icon: <LinkOffIcon />,
        },
      ]);
    }
    if (data.ProcessId) {
      options.push(
        {
          name: "Link Process",
          key: "link",
          onClick: () => linkData(data),
          icon: <LinkIcon />,
        },
        {
          name: "Edit Process",
          key: "toggle_status",
          onClick: () => editData(data),
          icon: <EditIcon />,
        },
        {
          name: "Delete Process",
          key: "toggle_status",
          onClick: () => inactiveData(data),
          icon: <DeleteIcon />,
        },
      );
    } else {
      options.push(
        getNewProcessMenuOption(data.AssetId, createData, <EditIcon />),
      );
    }

    return options;
  };

  const getGanttDelaysMenuOptions = (data: ProcessEvent) => {
    let options: MenuOptionType[] = [];

    options.push({
      name: "Justify Delay",
      key: "justify_delay",
      onClick: () => justifyData(data),
      icon: <Comment />,
    });
    options.push({
      name: "Split Delay",
      key: "split_delay",
      onClick: () => splitData(data),
      icon: <CallSplit />,
    });
    options.push({
      name: "See in Manager",
      key: "delays_manager",
      onClick: () => goToDelaysManager(data),
      icon: <MemoryIcon />,
    });

    return options;
  };

  //navigate functions
  const goToHeats = (ShiftId: number, HeatName: string) => {
    navigateWithPermission(
      `/meltshop-heats/${ShiftId}?heatName=${HeatName}`,
      "ui.navbar.meltshop.heats",
      ShiftId !== null,
    );
  };

  const goToCasterDataEntry = (ProcessId: number | null) => {
    navigateWithPermission(
      `/caster-data-entry/${ProcessId}`,
      "ui.navbar.caster.data-entry",
      ProcessId !== null,
    );
  };

  const goToLMFDataEntry = (ProcessId: number | null) => {
    navigateWithPermission(
      `/lmf-data-entry/${ProcessId}`,
      "ui.navbar.lmf.data-entry",
      ProcessId !== null,
    );
  };

  const goToEAFDataEntry = (ProcessId: number | null) => {
    navigateWithPermission(
      `/eaf-data-entry/${ProcessId}`,
      "ui.navbar.eaf.data-entry",
      ProcessId !== null,
    );
  };

  const goToTorchesDataEntry = (ProcessId: number | null) => {
    navigateWithPermission(
      `/caster-torches/${ProcessId}`,
      "ui.navbar.caster.torches",
      ProcessId !== null,
    );
  };

  const goToDelaysManager = async (delay: ProcessEvent) => {
    if (
      (delay.id === "11" && !hasPermission("ui.navbar.eaf.delays-manager")) ||
      (delay.id === "13" && !hasPermission("ui.navbar.caster.delays-manager"))
    ) {
      setError("You don't have permission to see this page");
      return;
    }
    const resp = await getDelayById(delay.DelayId as number);
    if (resp.ok) {
      const delay = resp.data;
      navigate(
        `/delays-manager/${delay?.ShiftId}?assetId=${delay?.AssetId}&delayId=${delay?.DelayId}`,
      );
    } else navigate(`/delays-manager/${shiftInfo?.CurrentShiftId}`);
  };

  const goToHeatReport = (data: Process) => {
    let assetType = "";
    let treatmentNumber: number | undefined = undefined;
    switch (data.ProcessCode) {
      case "C":
        assetType = "caster";
        break;
      case "L":
        assetType = "lmf";
        if (
          data.TreatmentNumber !== undefined &&
          data.TreatmentNumber !== null
        ) {
          treatmentNumber = data.TreatmentNumber;
        }
        break;
      case "E":
        assetType = "eaf";
        break;
      default:
        assetType = "eaf";
    }
    const params = new URLSearchParams({
      heatId: String(data.HeatId),
      processId: String(data.ProcessId),
    });
    if (typeof treatmentNumber === "number") {
      params.append("treatmentNumber", String(treatmentNumber));
    }
    navigate(`/heat-process-report/${assetType}?${params.toString()}`);
  };

  return {
    createData,
    editData,
    linkData,
    unlinkData,
    getGanttMenuOptions,
    getGanttDelaysMenuOptions,
    goToHeats,
    goToCasterDataEntry,
    showModal,
    showModalDelay,
    messageActiveModal,
    messageDeleteModal,
    messageUnlinkModal,
    setShowModal,
    setShowModalDelay,
    setMessageActiveModal,
    setMessageDeleteModal,
    setMessageUnlinkModal,
    error,
    setError,
  };
};
